import { css, RuleSet } from 'styled-components';

/**
 * Shared button primitive — the `.btn` recipe from the design system.
 *
 * One height (--h-btn), one radius (--r-sm), one type size. The four button
 * components differ only in which colour variant they default to:
 *
 *   PrimaryButton  → primary      DangerButton   → danger
 *   SecundButton   → primary      OutlineButton  → secondary
 *
 * Any call site can override that default through `className`, which is a
 * free channel — no call site used it before the redesign:
 *
 *   className="secondary"  "ghost"  "danger"  "primary"   colour
 *   className="sm"                                        --h-btn-sm
 *   className="icon"                                      square, icon only
 *
 * The variant blocks are nested selectors (&.secondary) rather than global
 * classes, so styled-components emits `.hash.secondary` — specificity 0,2,0
 * against the base's 0,1,0. The cascade resolves by weight, not source order,
 * and every rule for a button lives in one place.
 */

export const btnBase = css`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: var(--s2);
  height: var(--h-btn);
  padding: 0 var(--s4);
  border: 1px solid transparent;
  border-radius: var(--r-sm);
  font-family: var(--font-ui);
  font-size: var(--fs-body);
  font-weight: var(--fw-medium);
  line-height: 1;
  white-space: nowrap;
  text-decoration: none;
  cursor: pointer;
  transition:
    background-color 0.12s,
    border-color 0.12s,
    color 0.12s;

  svg {
    width: 15px;
    height: 15px;
    stroke-width: 1.9;
  }

  &:disabled,
  &.disabled {
    opacity: 0.45;
    cursor: not-allowed;
  }
`;

/* ── colour variants ─────────────────────────────────────────────────── */

export const primary = css`
  background: var(--primary);
  color: var(--text-invert);

  &:hover:not(:disabled):not(.disabled) {
    background: var(--primary-hover);
  }
`;

export const secondary = css`
  background: var(--surface);
  border-color: var(--line-strong);
  color: var(--text);

  &:hover:not(:disabled):not(.disabled) {
    background: var(--surface-alt);
  }
`;

export const ghost = css`
  background: transparent;
  color: var(--text-muted);

  &:hover:not(:disabled):not(.disabled) {
    background: var(--surface-alt);
    color: var(--text);
  }
`;

export const danger = css`
  background: var(--surface);
  border-color: var(--danger-line);
  color: var(--danger);

  &:hover:not(:disabled):not(.disabled) {
    background: var(--danger-soft);
  }
`;

/* ── modifiers, applied through className ────────────────────────────── */

export const btnVariants = css`
  &.primary {
    ${primary}
  }

  &.secondary {
    ${secondary}
  }

  &.ghost {
    ${ghost}
  }

  &.danger {
    ${danger}
  }

  &.sm {
    height: var(--h-btn-sm);
    padding: 0 var(--s3);
    font-size: var(--fs-label);
  }

  &.icon {
    width: var(--h-btn);
    padding: 0;
    flex: 0 0 var(--h-btn);
  }

  &.icon.sm {
    width: var(--h-btn-sm);
    flex-basis: var(--h-btn-sm);
  }
`;

/** Base + modifiers for a `<button>`. Pass the default colour variant in. */
export const btn = (base: RuleSet<object>) => css`
  ${btnBase}
  ${base}
  ${btnVariants}
`;

/** Same recipe applied to the `<a>` inside a link-mode wrapper. */
export const btnLink = (base: RuleSet<object>) => css`
  display: inline-flex;

  a {
    ${btnBase}
    ${base}
  }

  &.primary a {
    ${primary}
  }
  &.secondary a {
    ${secondary}
  }
  &.ghost a {
    ${ghost}
  }
  &.danger a {
    ${danger}
  }

  &.sm a {
    height: var(--h-btn-sm);
    padding: 0 var(--s3);
    font-size: var(--fs-label);
  }

  &.icon a {
    width: var(--h-btn);
    padding: 0;
  }

  &.icon.sm a {
    width: var(--h-btn-sm);
  }
`;
